import { useMemo } from 'react';

import { useQuery } from '@apollo/client/react';
import { Alert } from '@mui/material';

import {
  PropertyFiles,
  type PropertyFilesColumns,
} from '@modules/_shared/components/PropertyFiles';
import {
  GET_LISTING_DETAILS,
  LISTING_DOCUMENTS,
} from '@modules/listings/lotsQueries';
import { useLocale } from '@shared/hooks/locale';

type ListingDocumentsProps = {
  lotId: string;
  readOnly?: boolean;
  hideColumns?: PropertyFilesColumns[];
  onUpdate?: () => void;
  acceptedMimetypes?: string[];
  acceptLabel?: string;
};

export const ListingDocuments = ({
  lotId,
  readOnly = false,
  hideColumns,
  onUpdate,
  acceptedMimetypes,
  acceptLabel,
}: ListingDocumentsProps) => {
  const { t } = useLocale();
  const { data, loading, refetch, error } = useQuery(LISTING_DOCUMENTS, {
    variables: { lotId },
  });

  const propertyId = data?.lots_by_pk?.property_id;
  const propertyFiles = useMemo(
    () => data?.lots_by_pk?.property.property_files ?? [],
    [data?.lots_by_pk?.property.property_files],
  );

  if (error != null) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  if (!loading && propertyId == null) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {t('No property found for this listing')}
      </Alert>
    );
  }

  return (
    <PropertyFiles
      propertyId={propertyId}
      files={propertyFiles}
      readOnly={readOnly}
      refetchQueries={[
        LISTING_DOCUMENTS,
        { query: GET_LISTING_DETAILS, variables: { id: lotId } },
      ]}
      onDelete={async () => {
        await refetch();
        onUpdate?.();
      }}
      onUpdate={onUpdate}
      loading={loading}
      hideColumns={hideColumns}
      acceptedMimetypes={acceptedMimetypes}
      acceptLabel={acceptLabel}
    />
  );
};
